import { useTranslation } from "react-i18next";
import { ArrowLeft, BookmarkPlus, Check } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import { Meditation } from "../../domain/types";
import MeditationDetails from "./MeditationDetails";

type Props = {
    meditation: Meditation;
    onBack: () => void;
    /** Takes a copy into the therapist's own library, where it can be edited. */
    onAdd: () => void;
    adding: boolean;
    /** Opens the copy the therapist already took, when they took one. */
    onOpenCopy: (copyId: string) => void;
};

/**
 * Someone else's meditation, as it stands.
 *
 * Read-only because it is not yours: a published meditation is another
 * therapist's work, and edits made on this screen would either change what
 * everyone else sees or be quietly thrown away. Neither is worth the
 * convenience.
 *
 * Adding it to your library is the same screen's one action, so the therapist
 * who wanted to change a line reaches their own copy in one press rather than
 * finding out here that they cannot type.
 */
export default function MeditationReadOnlyView({
    meditation,
    onBack,
    onAdd,
    adding,
    onOpenCopy,
}: Props) {
    const { t } = useTranslation();

    return (
        <>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <button
                    type="button"
                    onClick={onBack}
                    className="flex min-h-11 items-center gap-2 text-sm font-medium text-content-secondary hover:text-content-primary"
                >
                    <ArrowLeft size={16} aria-hidden="true" />
                    {t("meditation.backToLibrary")}
                </button>

                {meditation.my_copy_id ? (
                    <Button
                        variant="outline"
                        startIcon={<Check size={16} />}
                        onClick={() => onOpenCopy(meditation.my_copy_id as string)}
                    >
                        {t("meditation.openCopy")}
                    </Button>
                ) : (
                    <Button
                        startIcon={<BookmarkPlus size={16} />}
                        loading={adding}
                        onClick={onAdd}
                    >
                        {t("meditation.copyToLibrary")}
                    </Button>
                )}
            </div>

            <div className="mx-auto flex max-w-3xl flex-col gap-5">
                <MeditationDetails meditation={meditation} />

                <p className="text-xs text-content-tertiary">
                    {t("meditation.readOnlyNotice")}
                </p>
            </div>
        </>
    );
}
